import { Button } from "@/components/ui/button";
import { ClipboardList, PlayCircle, Award, ClipboardCheck, RotateCcw, ArrowRight } from "lucide-react";
import { FurtherReading } from "@/components/FurtherReading";
import { SaveSession } from "@/components/SaveSession";

export const AdmissionIntro = ({ caseCount, onStart }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-sm fade-up text-center max-w-2xl mx-auto">
    <div className="w-14 h-14 rounded-xl bg-[#0047AB]/10 flex items-center justify-center mx-auto">
      <ClipboardList className="w-7 h-7 text-[#0047AB]" />
    </div>
    <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-4">
      Admissions Desk
    </h1>
    <p className="text-base text-slate-600 mt-3 leading-relaxed">
      Admit a surgical or procedure patient for the day. Work through five staged beats —{" "}
      <span className="font-semibold text-slate-800">identity &amp; procedure</span>, consent &amp;
      finances, the <span className="font-semibold text-slate-800">pre-op safety check</span>,
      preparing &amp; housing, and the clinical handover — catching the safety, legal and clinical
      red flags along the way. Graded against a Cert IV admission rubric.
    </p>
    <div className="flex flex-wrap items-center justify-center gap-2 mt-5">
      {["Patient ID", "Informed consent", "Fasting & biosecurity", "Accurate weigh-in", "Safe handover"].map((t) => (
        <span key={t} className="text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded bg-slate-100 text-slate-600">
          {t}
        </span>
      ))}
    </div>
    <div className="mt-7">
      <Button onClick={onStart} data-testid="start-drill-btn" className="bg-[#0047AB] hover:bg-[#0a3f8f] text-white text-base px-6 py-5">
        <PlayCircle className="w-5 h-5 mr-2" /> Admit a patient · {caseCount} cases
      </Button>
    </div>
    <FurtherReading topic="admission" className="mt-8 text-left" />
  </div>
);

const bandStyle = (percent) =>
  percent >= 80
    ? { label: "Competent", text: "text-emerald-600", ring: "border-emerald-300 bg-emerald-50" }
    : percent >= 60
    ? { label: "Developing", text: "text-amber-600", ring: "border-amber-300 bg-amber-50" }
    : { label: "Not yet competent", text: "text-red-600", ring: "border-red-300 bg-red-50" };

export const AdmissionDebrief = ({ result, onNext, onRestart }) => {
  const { percent, breakdown, caseTitle } = result;
  const s = bandStyle(percent);

  return (
    <div className="space-y-5 fade-up">
      <div className="bg-white border border-emerald-200 rounded-xl p-6 shadow-lg" data-testid="admission-debrief">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-emerald-700 font-semibold mb-5">
          <ClipboardCheck className="w-4 h-4" />
          <span>Admission debrief · {caseTitle}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="flex items-center gap-5">
            <div className={`flex flex-col items-center justify-center w-24 h-24 rounded-2xl border-4 ${s.ring}`} data-testid="debrief-grade">
              <Award className={`w-5 h-5 ${s.text}`} />
              <span className={`text-4xl font-bold leading-none ${s.text}`} data-testid="debrief-percent">{percent}</span>
              <span className="text-[10px] text-slate-400 font-semibold">/100</span>
            </div>
            <div>
              <div className={`text-xl font-bold ${s.text}`} data-testid="debrief-band">{s.label}</div>
              <div className="text-xs text-slate-500 mt-1">Cert IV admission competency</div>
            </div>
          </div>

          <div className="lg:col-span-2" data-testid="debrief-breakdown">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Stage scores</div>
            <div className="space-y-2">
              {breakdown.map((b) => {
                const pct = Math.round((b.earned / b.max) * 100);
                return (
                  <div key={b.key} data-testid={`debrief-stage-${b.key}`}>
                    <div className="flex items-center justify-between text-xs mb-0.5">
                      <span className="text-slate-700 font-medium">{b.label}</span>
                      <span className="font-mono-clinical font-semibold text-slate-800">{b.earned}/{b.max}</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${pct}%`, backgroundColor: pct >= 80 ? "#10b981" : pct >= 50 ? "#f59e0b" : "#ef4444" }}
                      />
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{b.detail}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <Button onClick={onNext} data-testid="next-case-btn" className="bg-[#0047AB] hover:bg-[#0a3f8f] text-white">
            Next patient <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
          <Button onClick={onRestart} variant="outline" data-testid="restart-btn" className="text-slate-600">
            <RotateCcw className="w-4 h-4 mr-1.5" /> Back to start
          </Button>
        </div>
      </div>

      <SaveSession
        simulatorId="admission"
        simulatorName="Admissions Desk"
        score={percent}
        grade={s.label}
        summary={`${caseTitle} · ${s.label}`}
      />

      <FurtherReading topic="admission" />
    </div>
  );
};
